<template>
  <el-card shadow="never">
    <div slot="header" class="card-header">
      <span>店铺设置</span>
    </div>

    <el-form ref="formRef" :model="form" :rules="rules" label-width="120px" class="config-form">
      <el-form-item label="店铺名称" prop="storeName">
        <el-input v-model.trim="form.storeName" maxlength="60" show-word-limit />
      </el-form-item>

      <el-form-item label="店铺主图">
        <div class="upload-row">
          <el-input v-model.trim="form.storeImage" placeholder="请输入图片地址或直接上传" />
          <input ref="fileInput" type="file" accept="image/*" class="upload-input" @change="handleUpload" />
          <el-button :loading="uploading" @click="$refs.fileInput.click()">上传图片</el-button>
        </div>
        <el-image v-if="form.storeImage" class="preview-image" :src="form.storeImage" fit="cover" />
      </el-form-item>

      <el-form-item label="店铺地址" prop="storeAddress">
        <el-input v-model.trim="form.storeAddress" maxlength="120" show-word-limit />
      </el-form-item>

      <el-form-item label="联系电话" prop="storePhone">
        <el-input v-model.trim="form.storePhone" maxlength="20" />
      </el-form-item>

      <el-form-item label="店铺定位">
        <div class="location-panel">
          <div class="location-panel__row">
            <el-input :value="displayLatitude" placeholder="未选择纬度" readonly />
            <el-input :value="displayLongitude" placeholder="未选择经度" readonly />
          </div>
          <div class="location-panel__actions">
            <el-button type="primary" plain @click="openMapPicker">地图点选</el-button>
            <el-button @click="clearLocation">清空定位</el-button>
          </div>
          <div class="location-panel__tip">
            点击“地图点选”后，可搜索地址或直接点击地图选择位置，保存后小程序店铺页可直接打开地图导航。
          </div>
          <div v-if="selectedAddressText" class="location-panel__result">
            已选位置：{{ selectedAddressText }}
          </div>
        </div>
      </el-form-item>

      <el-form-item label="店铺简介">
        <el-input v-model.trim="form.storeIntro" type="textarea" :rows="5" maxlength="300" show-word-limit />
      </el-form-item>
    </el-form>

    <div class="preview-card">
      <div class="preview-card__header">小程序店铺页预览</div>
      <div class="preview-card__body">
        <el-image v-if="form.storeImage" class="preview-card__image" :src="form.storeImage" fit="cover" />
        <div v-else class="preview-card__image preview-card__image--empty">店铺主图</div>
        <div class="preview-card__title">{{ form.storeName || '邹城聚能教育' }}</div>
        <div class="preview-card__line">{{ form.storeAddress || '请在后台配置店铺地址' }}</div>
        <div class="preview-card__line">{{ form.storePhone || '13800138000' }}</div>
        <div class="preview-card__desc">{{ form.storeIntro || '欢迎来到邹城聚能教育，欢迎到店咨询课程与服务。' }}</div>
      </div>
    </div>

    <div class="footer-actions">
      <el-button :loading="loading" @click="loadData">重置</el-button>
      <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
    </div>

    <el-dialog
      title="地图点选店铺位置"
      :visible.sync="pickerVisible"
      width="980px"
      top="6vh"
      :close-on-click-modal="false"
      @opened="onPickerOpened"
      @closed="onPickerClosed"
    >
      <div v-if="!amapReady" class="map-status">
        <div class="map-status__title">{{ mapStatusText }}</div>
        <div v-if="!amapKey" class="map-status__tip">
          请在后台项目根目录新增 `.env.local`，配置 `VUE_APP_AMAP_KEY=你的高德 Web Key`，重启后台前端后即可使用地图点选。
        </div>
      </div>

      <template v-else>
        <div class="map-toolbar">
          <el-input
            v-model.trim="searchKeyword"
            placeholder="搜索店铺地址、商圈或地标"
            clearable
            @keyup.enter.native="searchLocation"
          />
          <el-button :loading="searching" type="primary" @click="searchLocation">搜索</el-button>
          <el-button @click="useCurrentAddress">用当前店铺地址定位</el-button>
        </div>

        <div class="map-picker">
          <div id="store-map-picker" class="map-picker__canvas"></div>
          <div class="map-picker__side">
            <div class="map-picker__section">
              <div class="map-picker__section-title">选中位置</div>
              <div class="map-picker__info">
                <span>纬度</span>
                <strong>{{ tempLatitudeText }}</strong>
              </div>
              <div class="map-picker__info">
                <span>经度</span>
                <strong>{{ tempLongitudeText }}</strong>
              </div>
              <div class="map-picker__address">{{ tempAddress || '请点击地图选择位置' }}</div>
            </div>

            <div class="map-picker__section">
              <div class="map-picker__section-title">搜索结果</div>
              <div v-if="searchResults.length" class="search-results">
                <button
                  v-for="item in searchResults"
                  :key="item.id"
                  type="button"
                  class="search-results__item"
                  @click="selectSearchResult(item)"
                >
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.address || item.name }}</span>
                </button>
              </div>
              <div v-else class="search-results__empty">暂无结果，可直接点击地图完成选点。</div>
            </div>
          </div>
        </div>
      </template>

      <span slot="footer" class="dialog-footer">
        <el-button @click="pickerVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!hasTempLocation" @click="confirmLocation">确定使用此位置</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import { getStoreConfig, saveStoreConfig, uploadAdminImage } from '../../api/admin'
import { loadAmap } from '../../utils/amap'

const defaultForm = () => ({
  storeName: '邹城聚能教育',
  storeImage: '',
  storeAddress: '',
  storePhone: '13800138000',
  latitude: null,
  longitude: null,
  storeIntro: '欢迎来到邹城聚能教育，欢迎到店咨询课程与服务。'
})

export default {
  data() {
    return {
      form: defaultForm(),
      loading: false,
      saving: false,
      uploading: false,
      pickerVisible: false,
      amapReady: false,
      mapStatusText: '正在准备地图...',
      searchKeyword: '',
      searching: false,
      searchResults: [],
      tempLatitude: null,
      tempLongitude: null,
      tempAddress: '',
      selectedAddressText: '',
      map: null,
      marker: null,
      geocoder: null,
      placeSearch: null,
      amapKey: process.env.VUE_APP_AMAP_KEY || '',
      rules: {
        storeName: [{ required: true, message: '请输入店铺名称', trigger: 'blur' }],
        storeAddress: [{ required: true, message: '请输入店铺地址', trigger: 'blur' }],
        storePhone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }]
      }
    }
  },
  computed: {
    displayLatitude() {
      return this.form.latitude !== null && this.form.latitude !== undefined ? Number(this.form.latitude).toFixed(6) : ''
    },
    displayLongitude() {
      return this.form.longitude !== null && this.form.longitude !== undefined ? Number(this.form.longitude).toFixed(6) : ''
    },
    tempLatitudeText() {
      return this.tempLatitude !== null && this.tempLatitude !== undefined ? Number(this.tempLatitude).toFixed(6) : '--'
    },
    tempLongitudeText() {
      return this.tempLongitude !== null && this.tempLongitude !== undefined ? Number(this.tempLongitude).toFixed(6) : '--'
    },
    hasTempLocation() {
      return Number.isFinite(this.tempLatitude) && Number.isFinite(this.tempLongitude)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const data = await getStoreConfig()
        this.form = { ...defaultForm(), ...data }
        this.selectedAddressText = this.form.storeAddress || ''
      } catch (error) {
        this.$message.error(error?.message || '店铺设置加载失败')
      } finally {
        this.loading = false
      }
    },
    async handleUpload(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }
      this.uploading = true
      try {
        const data = await uploadAdminImage(file)
        this.form.storeImage = data.url
        this.$message.success('图片上传成功')
      } catch (error) {
        this.$message.error(error?.message || '图片上传失败')
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    },
    async openMapPicker() {
      this.pickerVisible = true
      this.mapStatusText = this.amapKey ? '正在准备地图...' : '未配置地图服务 Key'
    },
    async onPickerOpened() {
      if (!this.amapKey) {
        this.amapReady = false
        return
      }
      try {
        const AMap = await loadAmap(this.amapKey)
        this.amapReady = true
        this.$nextTick(() => {
          this.initMap(AMap)
        })
      } catch (error) {
        this.amapReady = false
        this.mapStatusText = '地图加载失败，请检查高德 Key 是否可用'
      }
    },
    onPickerClosed() {
      this.searchKeyword = ''
      this.searching = false
      this.searchResults = []
    },
    initMap(AMap) {
      if (!this.map) {
        this.map = new AMap.Map('store-map-picker', {
          zoom: 15,
          resizeEnable: true,
          center: this.getInitialCenter()
        })
        this.marker = new AMap.Marker({
          anchor: 'bottom-center'
        })
        this.geocoder = new AMap.Geocoder()
        this.placeSearch = new AMap.PlaceSearch({
          pageSize: 10,
          pageIndex: 1
        })
        this.map.on('click', event => {
          this.updateTempLocation(event.lnglat)
        })
      } else {
        this.map.resize()
        this.map.setCenter(this.getInitialCenter())
      }

      if (Number.isFinite(Number(this.form.latitude)) && Number.isFinite(Number(this.form.longitude))) {
        const lnglat = [Number(this.form.longitude), Number(this.form.latitude)]
        this.placeMarker(lnglat)
        this.tempLatitude = Number(this.form.latitude)
        this.tempLongitude = Number(this.form.longitude)
        this.tempAddress = this.form.storeAddress || ''
        this.map.setCenter(lnglat)
      } else {
        this.tempLatitude = null
        this.tempLongitude = null
        this.tempAddress = ''
        this.marker.setMap(null)
      }
    },
    getInitialCenter() {
      if (Number.isFinite(Number(this.form.latitude)) && Number.isFinite(Number(this.form.longitude))) {
        return [Number(this.form.longitude), Number(this.form.latitude)]
      }
      return [116.397428, 39.90923]
    },
    placeMarker(lnglat) {
      if (!this.marker || !this.map) {
        return
      }
      this.marker.setPosition(lnglat)
      this.marker.setMap(this.map)
      this.map.setCenter(lnglat)
    },
    updateTempLocation(lnglat) {
      const lng = Number(lnglat.lng)
      const lat = Number(lnglat.lat)
      this.tempLatitude = lat
      this.tempLongitude = lng
      this.placeMarker([lng, lat])
      this.reverseGeocode(lng, lat)
    },
    reverseGeocode(lng, lat) {
      if (!this.geocoder) {
        return
      }
      this.geocoder.getAddress([lng, lat], (status, result) => {
        if (status === 'complete' && result.regeocode) {
          this.tempAddress = result.regeocode.formattedAddress || ''
        }
      })
    },
    searchLocation() {
      if (!this.searchKeyword) {
        this.$message.warning('请输入要搜索的地址或地点')
        return
      }
      if (!this.placeSearch) {
        return
      }
      this.searching = true
      this.placeSearch.search(this.searchKeyword, (status, result) => {
        this.searching = false
        const pois = status === 'complete' && result?.poiList?.pois ? result.poiList.pois : []
        this.searchResults = pois.map(item => ({
          id: item.id || `${item.name}-${item.location?.lng}-${item.location?.lat}`,
          name: item.name,
          address: item.address,
          lng: item.location?.lng,
          lat: item.location?.lat
        })).filter(item => item.lng !== undefined && item.lat !== undefined)
        if (!this.searchResults.length) {
          this.$message.warning('没有找到匹配位置，请换个关键词试试')
        }
      })
    },
    selectSearchResult(item) {
      this.tempLatitude = Number(item.lat)
      this.tempLongitude = Number(item.lng)
      this.tempAddress = item.address || item.name || ''
      this.placeMarker([this.tempLongitude, this.tempLatitude])
    },
    useCurrentAddress() {
      if (!this.form.storeAddress) {
        this.$message.warning('请先填写店铺地址')
        return
      }
      this.searchKeyword = this.form.storeAddress
      this.searchLocation()
    },
    clearLocation() {
      this.form.latitude = null
      this.form.longitude = null
      this.selectedAddressText = ''
    },
    confirmLocation() {
      if (this.tempLatitude === null || this.tempLongitude === null) {
        this.$message.warning('请先在地图上选择位置')
        return
      }
      this.form.latitude = Number(this.tempLatitude.toFixed(6))
      this.form.longitude = Number(this.tempLongitude.toFixed(6))
      if (this.tempAddress) {
        this.form.storeAddress = this.tempAddress
        this.selectedAddressText = this.tempAddress
      }
      this.pickerVisible = false
      this.$message.success('店铺定位已更新')
    },
    submit() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) {
          return
        }
        this.saving = true
        try {
          await saveStoreConfig(this.form)
          this.$message.success('店铺设置已保存')
        } catch (error) {
          this.$message.error(error?.message || '店铺设置保存失败')
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.card-header {
  font-weight: 600;
}

.config-form {
  max-width: 760px;
}

.upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-input {
  display: none;
}

.preview-image {
  width: 140px;
  height: 140px;
  margin-top: 12px;
  border-radius: 12px;
}

.location-panel {
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  background: #fafaf9;
}

.location-panel__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.location-panel__actions {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.location-panel__tip {
  margin-top: 10px;
  color: #909399;
  line-height: 1.7;
  font-size: 13px;
}

.location-panel__result {
  margin-top: 10px;
  color: #2f6b4f;
  font-size: 13px;
}

.preview-card {
  width: 380px;
  margin-top: 24px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 20px;
  background: #fafaf9;
}

.preview-card__header {
  padding: 14px 18px;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  color: #303133;
  background: #fff;
}

.preview-card__body {
  padding: 18px;
}

.preview-card__image {
  width: 100%;
  height: 180px;
  border-radius: 16px;
}

.preview-card__image--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  color: #909399;
}

.preview-card__title {
  margin-top: 16px;
  font-size: 18px;
  font-weight: 700;
  color: #303133;
}

.preview-card__line {
  margin-top: 10px;
  color: #606266;
  line-height: 1.6;
}

.preview-card__desc {
  margin-top: 12px;
  color: #909399;
  line-height: 1.7;
}

.footer-actions {
  margin-top: 24px;
}

.map-status {
  display: flex;
  min-height: 340px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #606266;
}

.map-status__title {
  font-size: 16px;
  font-weight: 600;
}

.map-status__tip {
  max-width: 520px;
  margin-top: 12px;
  line-height: 1.8;
  color: #909399;
}

.map-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  margin-bottom: 16px;
}

.map-picker {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 16px;
}

.map-picker__canvas {
  min-height: 520px;
  border-radius: 16px;
  overflow: hidden;
  background: #f5f7fa;
}

.map-picker__side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.map-picker__section {
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 16px;
  background: #fafaf9;
}

.map-picker__section-title {
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.map-picker__info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #606266;
}

.map-picker__address {
  margin-top: 8px;
  line-height: 1.8;
  color: #909399;
  word-break: break-all;
}

.search-results {
  display: flex;
  max-height: 320px;
  flex-direction: column;
  gap: 10px;
  overflow: auto;
}

.search-results__item {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  background: #fff;
  color: #303133;
  text-align: left;
  cursor: pointer;
}

.search-results__item:hover {
  border-color: #2f6b4f;
}

.search-results__item span {
  color: #909399;
  line-height: 1.6;
}

.search-results__empty {
  color: #909399;
  line-height: 1.8;
}
</style>
